import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useWeb } from '../context/WebContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { FaPhoneAlt, FaWhatsapp, FaShieldAlt, FaChevronRight, FaTimesCircle, FaCheck } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

export default function ServiceDetail() {
  const { id } = useParams();
  const { services, locations, settings, addEnquiry } = useWeb();
  const navigate = useNavigate();

  const service = services.find(s => s.id === id);

  // Fallback redirect if service doesn't exist
  useEffect(() => {
    if (!service) {
      navigate('/');
    }
  }, [service, navigate]);

  // Set SEO metadata dynamically using the service's details
  useDocumentMeta(
    service ? `${service.title} Installation Services | ${settings.companyName}` : '',
    service ? `${service.shortDesc} Get price guidelines, custom sizes, and book same-day safety net installation.` : '',
    service ? service.image : ''
  );

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: service ? service.title : '',
    location: '',
    message: ''
  });

  // Keep form service in sync with URL changes
  useEffect(() => {
    if (service) {
      setFormData(prev => ({ ...prev, service: service.title }));
    }
  }, [service]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.location) {
      toast.error('Please fill in all required fields (Name, Phone, and Location)');
      return;
    }
    addEnquiry(formData);
    toast.success(`Your booking enquiry for ${formData.service} has been submitted! Our specialist will call you shortly.`);
    setFormData({
      name: '',
      phone: '',
      email: '',
      service: service ? service.title : '',
      location: '',
      message: ''
    });
  };

  const handleWhatsappSubmit = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_click', {
        'event_category': 'Engagement',
        'event_label': 'WhatsApp Service Detail Submit'
      });
    }
    const cleanNum = settings.whatsappNumber.replace(/[^0-9]/g, '');
    const text = `Hi Vengamamba Enterprises, My name is ${formData.name || 'Customer'}. I want a quote for ${formData.service} in ${formData.location || 'my location'}. Phone: ${formData.phone || 'N/A'}.`;
    window.open(`https://api.whatsapp.com/send?phone=${cleanNum}&text=${encodeURIComponent(text)}`, '_blank');
  };

  if (!service) return null;

  return (
    <div className="bg-slate-50 py-12">
      <Toaster position="top-right" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb */}
        <nav className="flex space-x-2 items-center text-xs text-slate-500 mb-8 overflow-x-auto whitespace-nowrap py-1">
          <Link to="/" className="hover:text-orange-600">Home</Link>
          <FaChevronRight className="text-[10px]" />
          <span className="text-slate-400">Services</span>
          <FaChevronRight className="text-[10px]" />
          <span className="text-slate-800 font-semibold">{service.title}</span>
        </nav>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Service Details Panel */}
          <div className="lg:col-span-8 space-y-8">
            <div className="bg-white rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
              <div className="relative h-72 sm:h-96">
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <span className="bg-orange-600 text-white font-bold text-xs uppercase px-3 py-1 rounded-md mb-2 inline-block">
                    {service.priceGuideline}
                  </span>
                  <h1 className="text-2xl sm:text-4xl font-extrabold">{service.title}</h1>
                </div>
              </div>
              
              <div className="p-6 sm:p-8 space-y-6">
                <div className="space-y-4">
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Service Description</h2>
                  <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                    {service.longDesc}
                  </p>
                </div>

                {/* Key Benefits */}
                <div className="space-y-4 pt-6 border-t">
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900">Key Benefits & Features</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-start space-x-3 bg-slate-50 p-4 rounded-xl border border-slate-150">
                        <div className="bg-orange-100 text-orange-600 p-1.5 rounded-lg text-xs mt-0.5">
                          <FaCheck />
                        </div>
                        <span className="text-slate-700 text-xs sm:text-sm font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* FAQs Accordion */}
            {service.faqs && service.faqs.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 space-y-6 shadow-sm">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">Frequently Asked Questions</h3>
                <div className="space-y-4">
                  {service.faqs.map((faq, index) => (
                    <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-150 space-y-2">
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base">Q. {faq.q}</h4>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">A. {faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Enquiry Widget Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-800">
              <h3 className="text-lg font-bold text-white mb-1">Book Installation</h3>
              <p className="text-xs text-slate-400 mb-6">Request a free site inspection & measurement check.</p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Your Name *</label>
                  <input 
                    type="text" 
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs sm:text-sm text-white placeholder-slate-600 outline-none focus:border-orange-500 transition"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Phone Number *</label>
                  <input 
                    type="tel" 
                    placeholder="10 digit number"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs sm:text-sm text-white placeholder-slate-600 outline-none focus:border-orange-500 transition"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Location *</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs sm:text-sm text-white outline-none focus:border-orange-500 transition"
                    required
                  >
                    <option value="" disabled>Select City</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.name}>{l.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-1">Specific Requirements</label>
                  <textarea 
                    rows="3" 
                    placeholder="E.g., length, balconys, window sizing..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs sm:text-sm text-white placeholder-slate-600 outline-none focus:border-orange-500 transition resize-none"
                  ></textarea>
                </div>

                <div className="flex flex-col gap-3 pt-2">
                  <button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-xl transition shadow-lg text-xs sm:text-sm"
                  >
                    Request Call Now
                  </button>
                  <button 
                    type="button"
                    onClick={handleWhatsappSubmit}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl transition flex items-center justify-center space-x-2 text-xs sm:text-sm"
                  >
                    <FaWhatsapp className="text-base" />
                    <span>Enquire via WhatsApp</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Quick Contact Helpline Card */}
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
              <h4 className="font-bold text-slate-900 text-sm">Need Direct Help?</h4>
              <div className="space-y-3">
                <a 
                  href={`tel:${settings.phone1}`}
                  onClick={() => gtag_report_conversion()}
                  className="flex items-center space-x-3 bg-slate-900 hover:bg-slate-800 text-white p-3.5 rounded-xl transition"
                >
                  <FaPhoneAlt className="text-orange-500 text-sm" />
                  <div className="text-left">
                    <span className="block text-[10px] uppercase text-slate-400 font-bold">Call Helpline</span>
                    <span className="text-xs sm:text-sm font-semibold">{settings.phone1}</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
